// @flow

import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
  Redirect,
  withRouter,
} from 'react-router-dom';
import {touch, isValid, getFormValues, reset as resetForm} from 'redux-form';
import {
  setRelatedProductType,
  filterArchived,
  fetchProduct,
  updateProduct,
  createProduct,
  updateProductVariant,
  reset,
  setActiveVariant,
  variantImage,
  setActiveVariantImage,
  setDefaultVariantImage,
  removeVariantImage,
  toggleReplacementDialog,
  fetchSuppliers,
  fetchRoomTypes,
  fetchWorkerTypes,
  fetchProductTypes,
  relatedProductSelected,
  relatedVariantSelected,
  relatedProductToggle,
  relatedProductTypeToggle,
  addRelatedProductType,
  clear,
  sharedRelatedProductSelected,
  sharedRelatedVariantSelected,
  sharedRelatedProductToggle,
  sharedRelatedProductTypeToggle,
  addSharedRelatedProductType,
  setSharedRelatedProductType,
  updateProductVariants,
  relatedProductTypeSelected,
  sharedRelatedProductTypeSelected,
  removeProductType,
  sharedRemoveProductType,
  unlockShared,
  setActiveVariantLinkedAdditionalVariant,
  addVariantLinkedAdditionalVariant,
  updateVariantLinkedAdditionalVariant,
  removeVariantLinkedAdditionalVariant,
  setActiveProductLinkedAdditionalVariant,
  addProductLinkedAdditionalVariant,
  updateProductLinkedAdditionalVariant,
  removeProductLinkedAdditionalVariant,
} from '../../actions/productView';
import {setDirty} from '../../actions/form';
import ProductForm from '../../components/ProductForm/ProductForm';
import DisableContent from '../../components/DisableContent/DisableContent';
import ProductVariantForm from '../../components/ProductVariantForm/ProductVariantForm';
import ProductVariantImages from '../../components/ProductVariantImages/ProductVariantImages';
import ServerError from '../../components/ServerError/ServerError';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import FormTabs from '../../components/FormTabs/FormTabs';
import SaveMenu from '../../components/SaveMenu/SaveMenu';
import Navigation from '../../components/Navigation/Navigation';
import ActionConfirmation from '../ActionConfirmation/ActionConfirmation';
import LinkedAdditionalVariantsContainer from '../LinkedAdditionalVariantsContainer/LinkedAdditionalVariantsContainer';
import validator from '../../helpers/validator';
import './ProductContainer.css';
import _ from 'lodash/fp';

const productValidation = {
  name: {rule: 'string|required', label: 'Artikelnamn'},
  productTypeId: {rule: 'numeric|required', label: 'Produkttyp'},
};

const variantValidation = {
  name: {rule: 'string|required', label: 'Namn'},
  itemNumber: {rule: 'string|required', label: 'Artikelnummer'},
  externalItemNumber: {rule: 'string', label: 'Externt artikelnummer'},
  externalDescription: {rule: 'string', label: 'Extern beskrivning'},
  internalDescription: {rule: 'string', label: 'Intern beskrivning'},
  color: {rule: 'string', label: 'Färg'},
  sizeWidth: {rule: 'numeric', label: 'Bredd'},
  sizeHeight: {rule: 'numeric', label: 'Höjd'},
  sizeDepth: {rule: 'numeric', label: 'Djup'},
  buyingPrice: {rule: 'numeric', label: 'Inköpspris'},
  sellingPrice: {rule: 'numeric|required', label: 'Försäljningspris'},
};

const mapRelated = _.flatMap(({
  id: productTypeId,
  name: productTypeName,
  products,
}) => _.flatMap(({
  id: productId,
  name: productName,
  variants,
}) => _.flow(
  _.filter('selected'),
  _.map(({id, name}) => ({
    id,
    name,
    productId,
    productName,
    productTypeId,
    productTypeName,
    selected: true,
  })),
)(variants), products));

export type Props = {
  filteredVariants: any[],
  includeArchived: boolean,
  filterArchived: typeof filterArchived,
  updateProduct: typeof updateProduct,
  createProduct: typeof createProduct,
  setActiveVariant: typeof setActiveVariant,
  setActiveVariantLinkedAdditionalVariant: Function,
  addVariantLinkedAdditionalVariant: typeof addVariantLinkedAdditionalVariant,
  updateVariantLinkedAdditionalVariant: typeof updateVariantLinkedAdditionalVariant,
  removeVariantLinkedAdditionalVariant: typeof removeVariantLinkedAdditionalVariant,
  setActiveProductLinkedAdditionalVariant: Function,
  addProductLinkedAdditionalVariant: typeof addProductLinkedAdditionalVariant,
  updateProductLinkedAdditionalVariant: typeof updateProductLinkedAdditionalVariant,
  removeProductLinkedAdditionalVariant: typeof removeProductLinkedAdditionalVariant,
  activeVariant: ?any,
  variantError: ?any,
  suppliersError: ?any,
  roomTypesError: ?any,
  workerTypesError: ?any,
  productTypesError: ?any,
  variantImage: typeof variantImage,
  setDefaultVariantImage: typeof setDefaultVariantImage,
  images: any[],
  imagesInProgress: any[],
  activeImage: ?any,
  productLinkedAdditionalVariants: any[],
  variantLinkedAdditionalVariants: any[],
  variants: any[],
  roomTypes: any[],
  workerTypes: any[],
  filteredRelatedProductTypes: any[],
  filteredProductTypes: any[],
  relatedProductType: ?{ id: number },
  setRelatedProductType: typeof setRelatedProductType,
  addRelatedProductType: typeof addRelatedProductType,
  isFetching: boolean,
  productIsDirty: boolean,
  variantIsDirty: boolean,
  variantIsFetching: boolean,
  suppliersIsFetching: boolean,
  roomTypesIsFetching: boolean,
  workerTypesIsFetching: boolean,
  productTypesIsFetching: boolean,
  resourcePath: string,
  fetchData: (id: any, variantId: any, copy: any) => any,
  toggleReplacementDialog: typeof toggleReplacementDialog,
  clear: typeof clear,
  product: ?any,
  productFormValues: any,
  productFormIsValid: boolean,
  variantFormValues: any,
  variantFormIsValid: boolean,
  params: any,
  error: ?any,
  updateProductVariant: typeof updateProductVariant,
  setActiveVariantImage: typeof setActiveVariantImage,
  resetForm: typeof resetForm,
  removeImage: typeof removeVariantImage,
  touch: typeof touch,
  suppliers: any[],
  setDirty: typeof setDirty,
  relatedError: ?any,
  relatedFetching: boolean,
  related: any[],
  relatedProductSelected: typeof relatedProductSelected,
  relatedVariantSelected: typeof relatedVariantSelected,
  relatedProductToggle: typeof relatedProductToggle,
  relatedProductTypeToggle: typeof relatedProductTypeToggle,
  reset: typeof reset,
  sharedRelated: any[],
  sharedRelatedProductSelected: typeof sharedRelatedProductSelected,
  sharedRelatedVariantSelected: typeof sharedRelatedVariantSelected,
  sharedRelatedProductToggle: typeof sharedRelatedProductToggle,
  sharedRelatedProductTypeToggle: typeof sharedRelatedProductTypeToggle,
  filteredSharedRelatedProductTypes: any[],
  sharedRelatedProductType: ?{ id: number },
  setSharedRelatedProductType: typeof setSharedRelatedProductType,
  addSharedRelatedProductType: typeof addSharedRelatedProductType,
  allRelated: Object,
  updateProductVariants: typeof updateProductVariants,
  relatedProductTypeSelected: typeof relatedProductTypeSelected,
  sharedRelatedProductTypeSelected: typeof sharedRelatedProductTypeSelected,
  removeProductType: typeof removeProductType,
  sharedRemoveProductType: typeof sharedRemoveProductType,
  unlockShared: typeof unlockShared,
  hasVariantDiffs: boolean,
  history: Object,
  activeProductLinkedAdditionalVariant: ?any,
  activeVariantLinkedAdditionalVariant: ?any,
  copy: boolean,
};

class ProductContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    if (!!this.props.params.variantId) {
      // eslint-disable-next-line
      this.props.history.replace(`${this.props.resourcePath}/${this.props.params.id}`);
    }

    this.props.fetchData(this.props.params.id, this.props.params.variantId, this.props.copy);
  }

  componentWillUnmount() {
    this.props.clear();
  }

  setDirty(form: 'product' | 'productVariant') {
    const {
      setDirty,
      productIsDirty,
      variantIsDirty,
    } = this.props;

    if (form === 'product' && !productIsDirty) {
      setDirty(true, form);
    } else if (form === 'productVariant' && !variantIsDirty) {
      setDirty(true, form);
    }
  }

  reset() {
    const {
      setDirty,
      reset,
      resetForm,
    } = this.props;
    reset();
    resetForm('product');
    resetForm('productVariant');
    setDirty(false, 'product');
    setDirty(false, 'productVariant');
  }

  save(copy) {
    const {
      productFormValues,
      variantFormValues,
      touch,
      variantFormIsValid,
      productFormIsValid,
      product,
      activeVariant,
      images,
      variantLinkedAdditionalVariants,
      productLinkedAdditionalVariants,
      related,
      updateProduct,
      createProduct,
      updateProductVariants,
      allRelated,
      variants,
    } = this.props;
    const productKeys = _.keys(productValidation);
    const variantKeys = _.keys(variantValidation);
    touch('product', ...productKeys);
    touch('productVariant', ...variantKeys);
    if (!variantFormIsValid || !productFormIsValid) {
      return;
    }

    const mainRelated = mapRelated(related);

    if (!product || copy) {
      createProduct({
        ...productFormValues,
        linkedAdditionalVariants: productLinkedAdditionalVariants,
        mainVariant: {
          ...variantFormValues,
          related: mainRelated,
          images,
          linkedAdditionalVariants: variantLinkedAdditionalVariants,
        },
        copy,
      });
    } else {
      updateProduct(product.id, {
        ...productFormValues,
        linkedAdditionalVariants: productLinkedAdditionalVariants,
      });
      updateProductVariants(product.id, [
        ..._.map(v => !!activeVariant && v.id === activeVariant.id ? {
          ...variantFormValues,
          related: mainRelated,
          images,
          linkedAdditionalVariants: variantLinkedAdditionalVariants,
        } : {
          ...v,
          related: mapRelated(allRelated[v.id] || []),
        }, variants),
        ...(!activeVariant ? [{
          ...variantFormValues,
          related: mainRelated,
          images,
          linkedAdditionalVariants: variantLinkedAdditionalVariants,
        }] : []),
      ]);
    }
  }

  render() {
    const {
      params: {id},
      filteredVariants,
      includeArchived,
      filterArchived,
      product,
      error,
      variantError,
      suppliersError,
      roomTypesError,
      workerTypesError,
      productTypesError,
      isFetching,
      variantIsFetching,
      suppliersIsFetching,
      roomTypesIsFetching,
      workerTypesIsFetching,
      productTypesIsFetching,
      resourcePath,
      variants,
      setActiveVariant,
      setActiveVariantImage,
      setDefaultVariantImage,
      setRelatedProductType,
      variantImage,
      removeImage,
      images,
      imagesInProgress,
      activeImage,
      productLinkedAdditionalVariants,
      variantLinkedAdditionalVariants,
      activeVariant,
      roomTypes,
      workerTypes,
      filteredRelatedProductTypes,
      filteredProductTypes,
      relatedProductType,
      updateProduct,
      updateProductVariant,
      toggleReplacementDialog,
      setActiveVariantLinkedAdditionalVariant,
      addVariantLinkedAdditionalVariant,
      updateVariantLinkedAdditionalVariant,
      removeVariantLinkedAdditionalVariant,
      setActiveProductLinkedAdditionalVariant,
      addProductLinkedAdditionalVariant,
      updateProductLinkedAdditionalVariant,
      removeProductLinkedAdditionalVariant,
      suppliers,
      productIsDirty,
      relatedError,
      relatedFetching,
      related,
      relatedProductSelected,
      relatedVariantSelected,
      relatedProductToggle,
      relatedProductTypeToggle,
      addRelatedProductType,
      variantIsDirty,
      sharedRelated,
      sharedRelatedProductSelected,
      sharedRelatedVariantSelected,
      sharedRelatedProductToggle,
      sharedRelatedProductTypeToggle,
      addSharedRelatedProductType,
      setSharedRelatedProductType,
      filteredSharedRelatedProductTypes,
      sharedRelatedProductType,
      relatedProductTypeSelected,
      sharedRelatedProductTypeSelected,
      removeProductType,
      sharedRemoveProductType,
      unlockShared,
      hasVariantDiffs,
      activeProductLinkedAdditionalVariant,
      activeVariantLinkedAdditionalVariant,
      copy,
    } = this.props;

    return (
      <div className="relative">
        <div className="row align-justify align-middle content-header">
          <div className="column shrink">
            <div className="row no-row-margins align-middle">
              <Navigation
                hideNavigation={true} />
              <div className="content-title">
                {copy ? 'Kopiera produkt' : (product && product.name) || 'Ny produkt'}
              </div>
            </div>
          </div>
          <div className="column shrink horizontal-button-list">
            {!copy && product &&
              <DisableContent disabled={productIsDirty}>
                <button
                  className="secondary button"
                  type="button"
                  onClick={() => {
                    this.props.history.push(`${this.props.resourcePath}/${this.props.params.id}/copy`);
                    // setDirty(true, 'product');
                    // setDirty(true, 'productVariant');
                  }}>
                  Kopiera
                </button>
                <ActionConfirmation
                  id="archiveProduct"
                  className="secondary button"
                  message={`Vill du verkligen ${product.archived ? 'av' : ''}arkivera den här produkten?`}
                  buttonText={product.archived ? 'Avarkivera' : 'Arkivera'}
                  action={() => updateProduct(product.id, {archived: !product.archived})} />
              </DisableContent>
            }
            {/* {copy &&
            <
            } */}
          </div>
        </div>
        <LoadingIndicator isLoading={
          isFetching
          || variantIsFetching
          || suppliersIsFetching
          || roomTypesIsFetching
          || workerTypesIsFetching
          || productTypesIsFetching
          || relatedFetching
        } />
        <ServerError error={
          error
          || suppliersError
          || roomTypesError
          || workerTypesError
          || productTypesError
          || relatedError
        } />
        <div className="content-section box">
          <div className="box-section">
            <ProductForm
              related={sharedRelated}
              relatedProductTypes={filteredSharedRelatedProductTypes}
              relatedProductType={sharedRelatedProductType}
              setRelatedProductType={setSharedRelatedProductType}
              relatedProductSelected={(id, productType, selected) => {
                sharedRelatedProductSelected(id, productType, selected);
                this.setDirty('productVariant');
              }}
              relatedVariantSelected={(id, productType, selected) => {
                sharedRelatedVariantSelected(id, productType, selected);
                this.setDirty('productVariant');
              }}
              relatedProductToggle={sharedRelatedProductToggle}
              relatedProductTypeToggle={sharedRelatedProductTypeToggle}
              addRelatedProductType={type => {
                addSharedRelatedProductType(type);
                this.setDirty('productVariant');
              }}
              initialValues={product}
              roomTypes={roomTypes}
              workerTypes={workerTypes}
              productTypes={filteredProductTypes}
              variants={_.map(v => ({
                ...v,
                name: !!v.archived ? `${v.name} (arkiverad)` : v.name,
              }), filteredVariants)}
              suppliers={suppliers}
              onUserChange={() => this.setDirty('product')}
              validate={values => validator(productValidation, values)}
              relatedProductTypeSelected={(productType, selected) => {
                sharedRelatedProductTypeSelected(productType, selected);
                this.setDirty('productVariant');
              }}
              removeProductType={id => {
                sharedRemoveProductType(id);
                this.setDirty('productVariant');
              }}
              hasVariantDiffs={hasVariantDiffs}
              unlockShared={unlockShared} />
          </div>
          <div className="box-section">
            <span className="box-section-title">Kopplade tilläggsprodukter</span>
            <LinkedAdditionalVariantsContainer
              active={activeProductLinkedAdditionalVariant}
              existing={productLinkedAdditionalVariants}
              setActive={setActiveProductLinkedAdditionalVariant}
              addAdditional={a => {
                addProductLinkedAdditionalVariant(a);
                this.setDirty('product');
              }}
              updateAdditional={a => {
                updateProductLinkedAdditionalVariant(a);
                this.setDirty('product');
              }}
              removeAdditional={a => {
                removeProductLinkedAdditionalVariant(a);
                this.setDirty('product');
              }}
            />
          </div>
        </div>
        <ServerError error={variantError} />
        <div className="content-section">
          <div className="row no-row-margins">
            <div className="box-side-tabs shrink">
              <DisableContent disabled={variantIsDirty}>
                <FormTabs
                  includeArchived={includeArchived}
                  onArchivedChanged={filterArchived}
                  onSelect={t => setActiveVariant(_.find({id: t.id}, variants))}
                  onAdd={() => setActiveVariant(null)}
                  title="Varianter"
                  tabs={_.map(v => ({
                    id: v.id,
                    label: !!v.archived ? `${v.name} (arkiverad)` : v.name,
                    active: activeVariant && activeVariant.id === v.id,
                  }), includeArchived ? variants : filteredVariants)} />
              </DisableContent>
            </div>
            <div className="box expand">
              <div className="row align-justify align-middle box-header">
                <div className="column">
                  <span className="box-title">
                    {(activeVariant && activeVariant.name) || 'Ny variant'}
                  </span>
                </div>
                <div className="column shrink horizontal-button-list">
                  {activeVariant &&
                    <DisableContent disabled={variantIsDirty}>
                      <ActionConfirmation
                        id="archiveProductVariant"
                        className="secondary button"
                        message={`Vill du verkligen ${activeVariant.archived ? 'av' : ''}arkivera den här varianten?`}
                        buttonText={activeVariant.archived ? 'Avarkivera' : 'Arkivera'}
                        action={() => updateProductVariant(activeVariant.id, {archived: !activeVariant.archived})} />
                    </DisableContent>
                  }
                </div>
              </div>
              <div className="box-section">
                <ProductVariantForm
                  toggleReplacementDialog={toggleReplacementDialog}
                  initialValues={activeVariant}
                  activeVariant={activeVariant}
                  productTypes={filteredRelatedProductTypes}
                  relatedProductType={relatedProductType}
                  setRelatedProductType={setRelatedProductType}
                  related={related}
                  relatedProductSelected={(id, productType, selected) => {
                    const variantId = !!activeVariant ? activeVariant.id : 0;
                    relatedProductSelected(variantId, id, selected);
                    this.setDirty('productVariant');
                  }}
                  relatedVariantSelected={(id, productType, selected) => {
                    const variantId = !!activeVariant ? activeVariant.id : 0;
                    relatedVariantSelected(variantId, id, selected);
                    this.setDirty('productVariant');
                  }}
                  relatedProductToggle={product => {
                    const variantId = !!activeVariant ? activeVariant.id : 0;
                    relatedProductToggle(variantId, product);
                  }}
                  relatedProductTypeToggle={type => {
                    const variantId = !!activeVariant ? activeVariant.id : 0;
                    relatedProductTypeToggle(variantId, type);
                  }}
                  addRelatedProductType={type => {
                    const variantId = !!activeVariant ? activeVariant.id : 0;
                    addRelatedProductType(variantId, type);
                    this.setDirty('productVariant');
                  }}
                  onUserChange={() => this.setDirty('productVariant')}
                  validate={values => validator(variantValidation, values)}
                  relatedProductTypeSelected={(productType, selected) => {
                    const variantId = !!activeVariant ? activeVariant.id : 0;
                    relatedProductTypeSelected(variantId, productType.id, selected);
                    this.setDirty('productVariant');
                  }}
                  removeProductType={id => {
                    const variantId = !!activeVariant ? activeVariant.id : 0;
                    removeProductType(variantId, id);
                    this.setDirty('productVariant');
                  }}
                />
              </div>
              <div className="box-section">
                <span className="box-section-title">Kopplade tilläggsprodukter</span>
                <LinkedAdditionalVariantsContainer
                  active={activeVariantLinkedAdditionalVariant}
                  existing={variantLinkedAdditionalVariants}
                  setActive={setActiveVariantLinkedAdditionalVariant}
                  addAdditional={a => {
                    addVariantLinkedAdditionalVariant(a);
                    this.setDirty('productVariant');
                  }}
                  updateAdditional={a => {
                    updateVariantLinkedAdditionalVariant(a);
                    this.setDirty('productVariant');
                  }}
                  removeAdditional={a => {
                    removeVariantLinkedAdditionalVariant(a);
                    this.setDirty('productVariant');
                  }}
                />
              </div>
              <div className="box-section">
                <ProductVariantImages
                  images={images}
                  imagesInProgress={imagesInProgress}
                  activeImage={activeImage}
                  activeVariant={activeVariant}
                  onActiveImage={setActiveVariantImage}
                  onDefaultImage={image => {
                    setDefaultVariantImage(image);
                    this.setDirty('productVariant');
                  }}
                  onDrop={(variantId, data) => {
                    variantImage(variantId, data);
                    this.setDirty('productVariant');
                  }}
                  onRemoveImage={(variantId, imageId) => {
                    removeImage(variantId, imageId);
                    this.setDirty('productVariant');
                  }} />
              </div>
            </div>
          </div>
        </div>
        {(productIsDirty || variantIsDirty) &&
          <SaveMenu
            isLoading={isFetching || variantIsFetching}
            onDiscard={() => this.reset()}
            onSave={() => {
              this.save(copy);
            }}/>
        }
        {product && product.id !== _.toNumber(id) &&
          <Redirect to={`${resourcePath}/${product.id}`} />
        }
      </div>
    );
  }
}

const getImagesInProgress = (activeVariant, imageUploads, files) => {
  const {id = 0} = activeVariant || {};
  if (!imageUploads[id]) {
    return [];
  }

  return _.flow(
    _.map(i => files.byId[i]),
    _.filter(i => !!i && !i.metadata),
  )(imageUploads[id]);
};

const mapStateToProps = ({
  productView: {
    product: {
      active: activeProduct,
      includeArchived,
      filteredVariants,
      variants,
      error,
      isDirty: productIsDirty,
      isFetching,
      relatedProductType: sharedRelatedProductType,
      linkedAdditionalVariants: productLinkedAdditionalVariants,
    },
    suppliers: {
      filteredList: suppliers,
      error: suppliersError,
      isFetching: suppliersIsFetching,
    },
    roomTypes: {
      list: roomTypes,
      error: roomTypesError,
      isFetching: roomTypesIsFetching,
    },
    workerTypes: {
      list: workerTypes,
      error: workerTypesError,
      isFetching: workerTypesIsFetching,
    },
    productTypes: {
      relatedList: filteredRelatedProductTypes,
      filteredList: filteredProductTypes,
      error: productTypesError,
      isFetching: productTypesIsFetching,
      sharedRelatedList: filteredSharedRelatedProductTypes,
    },
    variant: {
      relatedProductType,
      related: {
        error: relatedError,
        isFetching: relatedFetching,
        list: related,
        shared: sharedRelated,
        hasVariantDiffs,
      },
      active: activeVariant,
      activeImage,
      images,
      uploads,
      linkedAdditionalVariants: variantLinkedAdditionalVariants,
      error: variantError,
      isDirty: variantIsDirty,
      isFetching: variantIsFetching,
    },
    activeProductLinkedAdditionalVariant,
    activeVariantLinkedAdditionalVariant,
  },
  form,
  files,
}) => ({
  product: activeProduct,
  variants,
  filteredVariants,
  includeArchived,
  activeVariant,
  activeImage,
  images,
  imagesInProgress: getImagesInProgress(activeVariant, uploads, files),
  productLinkedAdditionalVariants,
  variantLinkedAdditionalVariants,
  error,
  variantError,
  suppliersError,
  roomTypesError,
  workerTypesError,
  productTypesError,
  isFetching,
  variantIsFetching,
  suppliersIsFetching,
  roomTypesIsFetching,
  workerTypesIsFetching,
  productTypesIsFetching,
  variantFormIsValid: isValid('productVariant')({form}),
  productFormIsValid: isValid('product')({form}),
  variantFormValues: getFormValues('productVariant')({form}),
  productFormValues: getFormValues('product')({form}),
  roomTypes,
  workerTypes,
  filteredRelatedProductTypes,
  filteredProductTypes,
  relatedError,
  relatedFetching,
  related: related[!!activeVariant ? activeVariant.id : 0] || [],
  relatedProductType,
  suppliers,
  productIsDirty,
  variantIsDirty,
  sharedRelated,
  sharedRelatedProductType,
  filteredSharedRelatedProductTypes,
  allRelated: related,
  hasVariantDiffs,
  activeProductLinkedAdditionalVariant,
  activeVariantLinkedAdditionalVariant,
});

const mapDispatchToProps = dispatch => ({
  relatedProductSelected: (variantId, id, selected) =>
    dispatch(relatedProductSelected(variantId, id, selected)),
  relatedVariantSelected: (variantId, id, selected) =>
    dispatch(relatedVariantSelected(variantId, id, selected)),
  relatedProductToggle: (variantId, id) =>
    dispatch(relatedProductToggle(variantId, id)),
  relatedProductTypeToggle: (variantId, id) =>
    dispatch(relatedProductTypeToggle(variantId, id)),
  setRelatedProductType: productType =>
    dispatch(setRelatedProductType(productType)),
  addRelatedProductType: (variantId, productType) =>
    dispatch(addRelatedProductType(variantId, productType)),
  filterArchived: includeArchived =>
    dispatch(filterArchived(includeArchived)),
  updateProduct: (id, data) =>
    dispatch(updateProduct(id, data)),
  createProduct: data =>
    dispatch(createProduct(data)),
  updateProductVariant: (id, data) =>
    dispatch(updateProductVariant(id, data)),
  clear: () =>
    dispatch(clear()),
  setActiveVariant: data =>
    dispatch(setActiveVariant(data)),
  touch: (form, ...fields) =>
    dispatch(touch(form, ...fields)),
  variantImage: (variantId, [file, ...rest]) => {
    if (file) {
      dispatch(variantImage(variantId, file));
    }
  },
  setDefaultVariantImage: image =>
    dispatch(setDefaultVariantImage(image)),
  setActiveVariantImage: image =>
    dispatch(setActiveVariantImage(image)),
  removeImage: (variantId, imageId) =>
    dispatch(removeVariantImage(variantId, imageId)),
  fetchData: (id, variantId, copy) => {
    dispatch(fetchRoomTypes());
    dispatch(fetchWorkerTypes());
    dispatch(fetchProductTypes());
    dispatch(fetchSuppliers());
    if (id || copy) {
      dispatch(fetchProduct(id, variantId, copy));
    }
  },
  toggleReplacementDialog: show =>
    dispatch(toggleReplacementDialog(show)),
  setActiveVariantLinkedAdditionalVariant: additional =>
    dispatch(setActiveVariantLinkedAdditionalVariant(additional)),
  addVariantLinkedAdditionalVariant: additional =>
    dispatch(addVariantLinkedAdditionalVariant(additional)),
  updateVariantLinkedAdditionalVariant: additional =>
    dispatch(updateVariantLinkedAdditionalVariant(additional)),
  removeVariantLinkedAdditionalVariant: additional =>
    dispatch(removeVariantLinkedAdditionalVariant(additional)),
  setActiveProductLinkedAdditionalVariant: additional =>
    dispatch(setActiveProductLinkedAdditionalVariant(additional)),
  addProductLinkedAdditionalVariant: additional =>
    dispatch(addProductLinkedAdditionalVariant(additional)),
  updateProductLinkedAdditionalVariant: additional =>
    dispatch(updateProductLinkedAdditionalVariant(additional)),
  removeProductLinkedAdditionalVariant: additional =>
    dispatch(removeProductLinkedAdditionalVariant(additional)),
  setDirty: (dirty, form) =>
    dispatch(setDirty(dirty, form)),
  resetForm: form =>
    dispatch(resetForm(form)),
  reset: () =>
    dispatch(reset()),
  sharedRelatedProductSelected: (id, productType, selected) =>
    dispatch(sharedRelatedProductSelected(id, productType, selected)),
  sharedRelatedVariantSelected: (id, productType, selected) =>
    dispatch(sharedRelatedVariantSelected(id, productType, selected)),
  sharedRelatedProductToggle: id =>
    dispatch(sharedRelatedProductToggle(id)),
  sharedRelatedProductTypeToggle: id =>
    dispatch(sharedRelatedProductTypeToggle(id)),
  setSharedRelatedProductType: productType =>
    dispatch(setSharedRelatedProductType(productType)),
  addSharedRelatedProductType: productType =>
    dispatch(addSharedRelatedProductType(productType)),
  updateProductVariants: (id, data) =>
    dispatch(updateProductVariants(id, data)),
  relatedProductTypeSelected: (variantId, id, selected) =>
    dispatch(relatedProductTypeSelected(variantId, id, selected)),
  sharedRelatedProductTypeSelected: (productType, selected) =>
    dispatch(sharedRelatedProductTypeSelected(productType, selected)),
  removeProductType: (variantId, id) =>
    dispatch(removeProductType(variantId, id)),
  sharedRemoveProductType: id =>
    dispatch(sharedRemoveProductType(id)),
  unlockShared: () =>
    dispatch(unlockShared()),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProductContainer));



// WEBPACK FOOTER //
// ./src/containers/ProductContainer/ProductContainer.js